import React, { useMemo, useRef } from 'react';
import html2canvas from 'html2canvas';
import { GroupDimension, PrimaryGroup } from '../types';
import { daysFrom, fmtDate, TODAY } from '../utils/dateHelpers';
import { formatQty } from '../utils/formatHelpers';
import './ShareModal.scss';

interface ShareModalProps {
  group: PrimaryGroup;
  groupDimension: GroupDimension;
  onClose: () => void;
}

type EntryState = 'overdue' | 'today' | 'future';

const STATE_LABEL: Record<EntryState, string> = {
  overdue: 'Overdue',
  today: 'Today',
  future: 'Planned',
};

interface PlanEntry {
  date: string;
  qty: number;
  state: EntryState;
  vendorPoNumber: string;
  location: string;
}

interface PlanSkuRow {
  sku: string;
  unit: string;
  entries: PlanEntry[];
}

interface PlanGroup {
  label: string;
  skus: PlanSkuRow[];
}

export const ShareModal: React.FC<ShareModalProps> = ({
  group,
  groupDimension,
  onClose,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);

  // Always the complete picture for this vendor/client — the modal is
  // opened with the unfiltered group, so this reflects everything on the
  // books, not just whatever the current status/in-transit filters show.
  const planGroups: PlanGroup[] = useMemo(() => {
    return group.subGroups
      .map((sub) => {
        const skus: PlanSkuRow[] = sub.skuGroups
          .filter((sg) => !sg.onHold)
          .map((sg) => {
            const entries: PlanEntry[] = [];
            sg.pos.forEach((po) => {
              po.deliverySchedule.forEach((entry) => {
                const delta = daysFrom(entry.date);
                const state: EntryState =
                  delta > 0 ? 'overdue' : delta === 0 ? 'today' : 'future';
                entries.push({
                  date: entry.date,
                  qty: entry.qty,
                  state,
                  vendorPoNumber: po.vendorPoNumber,
                  location: po.city,
                });
              });
            });
            return { sku: sg.sku, unit: sg.unit, entries };
          })
          .filter((row) => row.entries.length > 0); // no plan ever set -> leave it off the shared schedule
        return { label: sub.label, skus };
      })
      .filter((g) => g.skus.length > 0);
  }, [group]);

  const asOfStr = TODAY.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleDownload = () => {
    if (!cardRef.current) return;
    const today = new Date();
    const dateStr = today.toISOString().slice(0, 10); // YYYY-MM-DD, actual day of download
    const safeName = group.key
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    html2canvas(cardRef.current, { backgroundColor: '#ffffff', scale: 2 }).then(
      (canvas) => {
        const link = document.createElement('a');
        link.download = `${safeName}_${dateStr}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
    );
  };

  return (
    <div className="share-modal-overlay" onClick={handleOverlayClick}>
      <div className="share-modal-shell">
        <div className="share-modal-toolbar">
          <button
            type="button"
            className="share-modal-toolbar__download"
            onClick={handleDownload}
          >
            ⬇ Download as image
          </button>
          <button
            type="button"
            className="share-modal-toolbar__close"
            onClick={onClose}
          >
            ✕ Close
          </button>
        </div>

        <div className="schedule-card" ref={cardRef}>
          <div className="schedule-card__brand-bar" />
          <div className="schedule-card__body">
            <div className="schedule-card__title">{group.key}</div>
            <div className="schedule-card__subtitle">
              Planned schedule · as of {asOfStr}
            </div>

            {planGroups.length === 0 ? (
              <div className="schedule-empty">
                Nothing currently planned for this {groupDimension}.
              </div>
            ) : (
              planGroups.map((g) => (
                <div className="plan-group" key={g.label}>
                  <div className="plan-group__title">{g.label}</div>
                  {g.skus.map((row) => (
                    <div className="plan-sku" key={row.sku}>
                      <div className="plan-sku__name">{row.sku}</div>
                      {row.entries.map((entry, i) => (
                        <div
                          className={`plan-entry is-${entry.state}`}
                          key={`${entry.date}-${i}`}
                        >
                          <span className="plan-entry__po">
                            {entry.vendorPoNumber}
                          </span>
                          <span className="plan-entry__qty">
                            {formatQty(entry.qty, row.unit)}
                          </span>
                          <span className="plan-entry__date">{entry.date}</span>
                          <span className="plan-entry__status">
                            {STATE_LABEL[entry.state]}
                          </span>
                          <span className="plan-entry__po">
                            {entry.location}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))
            )}

            <div className="schedule-card__footer">
              Xetgo Sourcing Standup Tracker
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
